'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { usePathname } from 'next/navigation';
import { Menu, X, Calendar, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { cn } from '@kit/ui/utils';
import { RSSHeaderAccountSection } from './header-account-section';

// All nav links (used in mobile)
const navLinks = [
  { labelKey: 'landing:nav.about', fallback: 'ABOUT', href: '/#about' },
  { labelKey: 'landing:nav.programs', fallback: 'PROGRAMS', href: '/#programs' },
  { labelKey: 'landing:nav.schedule', fallback: 'SCHEDULE', href: '/#schedule' },
  { labelKey: 'landing:nav.facilities', fallback: 'FACILITIES', href: '/#facilities' },
  { labelKey: 'landing:nav.venueBooking', fallback: 'VENUE BOOKING', href: '/#venue-booking' },
  { labelKey: 'landing:nav.location', fallback: 'LOCATION', href: '/#location' },
];

// Reduced nav links for authenticated desktop users
const authDesktopNavLinks = [
  { labelKey: 'landing:nav.programs', fallback: 'PROGRAMS', href: '/#programs' },
  { labelKey: 'landing:nav.schedule', fallback: 'SCHEDULE', href: '/#schedule' },
  { labelKey: 'landing:nav.location', fallback: 'LOCATION', href: '/#location' },
];

const authNavLinks = [
  { labelKey: 'landing:nav.myBookings', fallback: 'MY BOOKINGS', href: '/bookings', icon: Calendar },
  { labelKey: 'landing:nav.myPackages', fallback: 'MY PACKAGES', href: '/packages', icon: Package },
];

// Routes that should have a solid (non-transparent) navbar
const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/faq',
  '/event',
  '/coaches',
  '/membership',
];

export function RSSHeader() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useSession();
  const isAuthenticated = !!session.data;

  // Check if we're on the landing page (needs no spacer as hero handles it)
  const isLandingPage = pathname === '/';

  // Check if current route should have solid navbar
  const shouldBeSolid = solidNavbarRoutes.some(route => pathname?.startsWith(route));

  // Always use light text on dark background
  const usesDarkText = false;

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          mobileMenuOpen
            ? 'bg-[#293044]'
            : shouldBeSolid || scrolled
              ? 'bg-[#293044] shadow-lg'
              : 'bg-transparent'
        )}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="relative z-10 flex-shrink-0">
            <Image
              src={usesDarkText
                ? "/landing-pages/rss/images/Horizontal_Logo_Colored.svg"
                : "/landing-pages/rss/images/Horizontal_Logo_Negative.svg"
              }
              alt="Red Sea Sports Club"
              width={240}
              height={70}
              className="h-16 lg:h-20 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {/* Show reduced links for authenticated, full links for guests */}
            {(isAuthenticated ? authDesktopNavLinks : navLinks).map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "hover:text-[#ED253E] transition-colors duration-300 text-sm tracking-[1px] whitespace-nowrap",
                  usesDarkText ? "text-[#293044]/90" : "text-white/90"
                )}
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {/* Auth-only links: My Bookings & My Packages */}
            {isAuthenticated && authNavLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "hover:text-[#ED253E] transition-colors duration-300 text-sm tracking-[1px] whitespace-nowrap",
                  usesDarkText ? "text-[#293044]/90" : "text-white/90"
                )}
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2">
            <RSSLanguageToggle />
            <RSSHeaderAccountSection usesDarkText={usesDarkText} />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={cn("lg:hidden p-2 -mr-2", usesDarkText ? "text-[#293044]" : "text-white")}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay - closes menu when clicking outside */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 top-[56px] z-40 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        {/* Mobile Menu */}
        <div
          className={cn(
            'lg:hidden absolute top-full left-0 right-0 bg-[#293044] transition-all duration-300 overflow-hidden z-50',
            mobileMenuOpen ? 'max-h-[500px]' : 'max-h-0'
          )}
        >
          <nav className="flex flex-col pb-4 px-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/90 hover:text-[#ED253E] transition-colors duration-300 text-base tracking-[1px] py-3 border-b border-white/5"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated && (
              <>
                {authNavLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 text-white/90 hover:text-[#ED253E] transition-colors duration-300 text-base tracking-[1px] py-3 border-b border-white/5"
                    >
                      <Icon className="h-5 w-5" />
                      {t(link.labelKey, { defaultValue: link.fallback })}
                    </Link>
                  );
                })}
              </>
            )}
            <div className="pt-4 flex items-center gap-3">
              <RSSLanguageToggle />
              <div className="flex-1">
                <RSSHeaderAccountSection onNavigate={() => setMobileMenuOpen(false)} />
              </div>
            </div>
          </nav>
        </div>
      </header>
      {/* Spacer for fixed header - only on non-landing pages */}
      {!isLandingPage && <div className="h-16 lg:h-20" />}
    </>
  );
}

function RSSLanguageToggle() {
  const { i18n } = useTranslation();

  const locales = (i18n.options.supportedLngs as string[] | undefined ?? []).filter(
    (locale) => locale.toLowerCase() !== 'cimode',
  );

  const nextLocale =
    locales.find((locale) => locale !== i18n.language) ?? i18n.language;

  const nextLabel = nextLocale.toUpperCase();

  const switchLanguage = useCallback(async () => {
    await i18n.changeLanguage(nextLocale);
    window.location.reload();
  }, [i18n, nextLocale]);

  if (locales.length <= 1) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={switchLanguage}
      aria-label={`Switch language to ${nextLabel}`}
      className="px-3 py-2 text-sm font-semibold tracking-wide text-white/90 hover:text-[#ED253E] transition-colors duration-300"
    >
      {nextLabel}
    </button>
  );
}

function useSession() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: listener } = client.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
    });

    return () => listener.subscription.unsubscribe();
  }, [client.auth, queryClient]);

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}
